import { useEffect, useState, type FC } from "react";
import type { InvoiceFieldValidationModel } from "../../../../models/InvoiceModels";
import { useTranslation } from "react-i18next";
import Subheading from "../../../atoms/typography/Subheading";
import TextField from "../../../atoms/inputs/TextField";
import AutocompleteSelect from "../../../atoms/inputs/AutocompleteSelect";
import { COUNTRY_CODE_OPTIONS } from "../../../../utils/countryCodes";
import { Col, Row } from "react-grid-system";
import IconButton from "../../../atoms/buttons/IconButton";
import { SaveIconMedium } from "../../../atoms/icons/SaveIcon";
import BodySmall from "../../../atoms/typography/BodySmall";
import { theme } from "../../../../theme";
import { useUpdateInvoiceRecipient } from "../../../../graphql/hooks/invoices/useUpdateInvoiceRecipient";
import Alert from "../../../molecules/Alert";
import Tooltip from "../../../molecules/Tooltip";
import ChevronButton from "../../../atoms/buttons/ChevronButtons";
import { useRegisterSaveSection } from "../SaveSectionsContext";
import { SlideOutHeight } from "../../../atoms/SlideOutHeight";
import type { ClientModel } from "../../../../models/ClientModel";
import Lookup from "../../Lookup";
import { useLookup } from "../../../../graphql/hooks/useLookup";
import Small from "../../../atoms/typography/Small";
import styled from "styled-components";
import { AddIcon } from "../../../atoms/icons/AddIcon";
import Checkbox from "../../../atoms/inputs/Checkbox";
import { InfoIconSmall } from "../../../atoms/icons/InfoIcons";
import { HorizontalSpacer } from "../../../atoms/Spacers";
import { useExternalLookup } from "../../../../graphql/hooks/useExternalLookup";

interface RecipientData {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
  email?: string;
  clientIdentifier?: string;
  invoiceId: string;
  recipientNumber?: string;
  recipientName?: string;
  isForeignIdentifier?: boolean;
}

interface Props {
  recipientFields: Array<InvoiceFieldValidationModel>;
  invoiceId: string;
  invoiceRecipient?: {
    name?: string;
    clientIdentifier?: string;
    recipientEmail?: string;
    address1?: string;
    address2?: string;
    city?: string;
    countryCode?: string;
    customerCode?: string;
    zipCode?: string;
    client?: ClientModel;
    isForeignIdentifier?: boolean;
  };
  isEditable: boolean;
}

const ChipSelectDontainer = styled.button`
  display: flex;
  gap: 8px;
  padding: 4px 4px;
  border: none;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.grey05};
  background-color: ${theme.colors.white};
  cursor: pointer;
  align-items: center;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const InvoiceRecipientDetailsSection: FC<Props> = ({
  recipientFields,
  invoiceId,
  invoiceRecipient,
  isEditable,
}) => {
  const { t } = useTranslation();
  const [dataHasBeenUpdated, setDataHasBeenUpdated] = useState(false);

  const [recipientData, setRecipientData] = useState<RecipientData | null>(
    null,
  );
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setRecipientData({
      invoiceId: invoiceId,
      recipientName: invoiceRecipient?.name,
      clientIdentifier:
        invoiceRecipient?.client?.id ?? invoiceRecipient?.clientIdentifier,
      email: invoiceRecipient?.recipientEmail,
      addressLine1: invoiceRecipient?.address1,
      addressLine2: invoiceRecipient?.address2,
      postalCode: invoiceRecipient?.zipCode,
      city: invoiceRecipient?.city,
      countryCode: invoiceRecipient?.countryCode,
      recipientNumber: invoiceRecipient?.customerCode,
      isForeignIdentifier: invoiceRecipient?.isForeignIdentifier,
    });
  }, [invoiceId, invoiceRecipient]);

  const {
    lookup: lookupData,
    loading: lookupLoading,
    error: lookupError,
  } = useLookup({
    searchTerm: recipientData?.recipientName ?? "",
    skip: 0,
    take: 2,
  });
  const {
    lookup: lookupDataExternal,
    loading: lookupLoadingExternal,
    error: lookupErrorExternal,
  } = useExternalLookup(
    {
      searchTerm: recipientData?.recipientName ?? "",
      skip: 0,
      take: 2,
    },
    // Suggestions are only rendered while the recipient is editable and still
    // unmatched, so don't hit the external provider otherwise.
    !isEditable || !!recipientData?.clientIdentifier,
  );

  const onUpdateField = (key: keyof RecipientData, value: string) => {
    if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);
    setRecipientData((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
  };

  const onUpdateForeignIdentifier = (value: boolean) => {
    if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);
    setRecipientData((prev) => {
      if (!prev) return prev;
      return { ...prev, isForeignIdentifier: value };
    });
  };

  const onCompletedSave = (res?: any) => {
    if (res?.invoice?.updateInvoiceRecipient?.success) {
      setErrorMessage("");
      setSaveSuccessful(true);
      setTimeout(() => setSaveSuccessful(false), 5000);
      setDataHasBeenUpdated(false);
    } else {
      setErrorMessage(t("invoices.saveFailed"));
    }
  };
  const onErrorSave = (e?: any) => {
    console.error(e);
    setErrorMessage(e?.toString());
  };
  const { updateInvoiceRecipient, error, loading } = useUpdateInvoiceRecipient({
    onCompleted: onCompletedSave,
    onError: onErrorSave,
  });

  const onClickSaveSection = async () => {
    if (!recipientData) return;

    try {
      await updateInvoiceRecipient({
        variables: {
          input: recipientData,
        },
      });
    } catch (e) {
      console.error(e);
      setErrorMessage(e?.toString() || t("invoices.saveFailed"));
    }
  };

  useRegisterSaveSection({
    id: "recipient",
    label: t("invoiceValidation.saveRecipient"),
    isDirty: dataHasBeenUpdated,
    onSave: onClickSaveSection,
    loading,
  });

  // Maps fieldType to the original invoice recipient value for comparison.
  const invoiceRecipientValueByFieldType: Record<
    string,
    string | undefined | boolean
  > = {
    RecipientName: invoiceRecipient?.name,
    RecipientAddressLine1: invoiceRecipient?.address1,
    RecipientAddressLine2: invoiceRecipient?.address2,
    RecipientCity: invoiceRecipient?.city,
    RecipientPostalCode: invoiceRecipient?.zipCode,
    RecipientCountryCode: invoiceRecipient?.countryCode,
    RecipientEmail: invoiceRecipient?.recipientEmail,
    RecipientClientIdentifier:
      invoiceRecipient?.client?.id ?? invoiceRecipient?.clientIdentifier,
    RecipientNumber: invoiceRecipient?.customerCode,
    IsForeignIdentifier: invoiceRecipient?.isForeignIdentifier ?? false,
  };

  // Returns an error message if the field has a validation error AND the invoice
  // recipient value hasn't changed since the (invalid) validation was recorded.
  const getFieldError = (fieldType: string) => {
    const validationField = recipientFields.find(
      (f) => f.fieldType === fieldType,
    );
    if (!validationField || validationField.isValid) return undefined;

    if (
      fieldType === "RecipientClientIdentifier" &&
      !invoiceRecipient?.client?.id
    ) {
      return t("invoices.fieldIsInvalid", {
        fieldName: t(`invoices.${fieldType}`),
      });
    }

    const validationValue = validationField.value ?? "";
    const invoiceValue = invoiceRecipientValueByFieldType[fieldType] ?? "";

    if (validationValue === invoiceValue) {
      return t("invoices.fieldIsInvalid", {
        fieldName: t(`invoices.${fieldType}`),
      });
    }
    return undefined;
  };

  const onClickSuggestedLookupResult = (item: {
    clientIdentifier: string;
    companyName: string;
  }) => {
    onUpdateField("clientIdentifier", item.clientIdentifier);
    onUpdateField("recipientName", item.companyName);
  };

  return (
    <div
      style={{
        backgroundColor: dataHasBeenUpdated
          ? theme.colors.info05
          : "transparent",
        padding: 8,
        borderRadius: 4,
      }}
    >
      <Row nogutter justify="between">
        <Subheading>{t("common.recipient")}</Subheading>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ChevronButton
            type={open ? "up" : "down"}
            onClick={() => setOpen(!open)}
          />
          {saveSuccessful ? (
            <BodySmall fontweight={500} color={theme.colors.success}>
              {t("random.saved")}
            </BodySmall>
          ) : (
            <Tooltip text={t("invoiceValidation.saveSection")}>
              <IconButton
                icon={
                  <SaveIconMedium
                    color={
                      dataHasBeenUpdated
                        ? theme.colors.white
                        : theme.colors.text
                    }
                  />
                }
                onClick={onClickSaveSection}
                size="medium"
                loading={loading}
                color={
                  dataHasBeenUpdated ? theme.colors.info : theme.colors.info05
                }
                disabled={!isEditable}
              />
            </Tooltip>
          )}
        </div>
      </Row>
      <SlideOutHeight isVisible={open}>
        <Row>
          <Col
            xs={12}
            md={12}
            style={{
              padding: "8px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: getFieldError("RecipientClientIdentifier")
                  ? "center"
                  : "flex-end",
              }}
            >
              <TextField
                label={"Id (Org/Vat)"}
                value={recipientData?.clientIdentifier ?? ""}
                name={"clientIdentifier"}
                fullWidth
                onChange={(value) => onUpdateField("clientIdentifier", value)}
                errorMessage={getFieldError("RecipientClientIdentifier")}
                disabled={!isEditable}
              />
              {isEditable && (
                <Lookup
                  type="modal"
                  preselectedValue={recipientData?.recipientName}
                  onClickResult={(item: {
                    clientIdentifier: string;
                    companyName: string;
                  }) => {
                    onUpdateField("clientIdentifier", item.clientIdentifier);
                    onUpdateField("recipientName", item.companyName);
                  }}
                  canToggleSources={true}
                />
              )}
            </div>
            {!lookupError &&
              !lookupLoading &&
              recipientData?.recipientName &&
              !recipientData?.clientIdentifier &&
              lookupData &&
              isEditable && (
                <div
                  style={{
                    marginTop: 4,
                  }}
                >
                  {lookupData?.quickcompany?.items?.map((item) => (
                    <ChipSelectDontainer
                      key={item.id}
                      onClick={() => onClickSuggestedLookupResult(item)}
                    >
                      <AddIcon />
                      <div style={{ width: "100%" }}>
                        <Row nogutter justify="between">
                          <Small>{item.companyName}</Small>
                          <Small fontweight={400}>
                            {item.clientIdentifier}
                          </Small>
                        </Row>
                      </div>
                    </ChipSelectDontainer>
                  ))}
                </div>
              )}
            {!lookupErrorExternal &&
              !lookupLoadingExternal &&
              recipientData?.recipientName &&
              !recipientData?.clientIdentifier &&
              lookupDataExternal &&
              isEditable && (
                <div>
                  {lookupDataExternal?.externalQuickLookup?.items?.map(
                    (item) => (
                      <ChipSelectDontainer
                        key={item.id}
                        onClick={() =>
                          onClickSuggestedLookupResult({
                            clientIdentifier: item.identifier,
                            companyName: item.name,
                          })
                        }
                      >
                        <AddIcon />
                        <div style={{ width: "100%" }}>
                          <Row nogutter justify="between">
                            <Small>{item.name}</Small>
                            <Small fontweight={400}>{item.identifier}</Small>
                          </Row>
                          <Small
                            fontweight={400}
                            color={theme.colors.textMuted}
                          >
                            {item.address}
                          </Small>
                        </div>
                      </ChipSelectDontainer>
                    ),
                  )}
                </div>
              )}
            <HorizontalSpacer spacing={0.25} />
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 8,
              }}
            >
              <Checkbox
                small
                checked={recipientData?.isForeignIdentifier ?? false}
                onChange={() =>
                  onUpdateForeignIdentifier(!recipientData?.isForeignIdentifier)
                }
                title={t("invoices.foreignRecipient")}
                disabled={!isEditable}
              />
              <Tooltip text={t("invoices.foreignRecipientDescription")}>
                <InfoIconSmall />
              </Tooltip>
            </div>
          </Col>

          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("random.name")}
              value={recipientData?.recipientName ?? ""}
              name={"recipientName"}
              fullWidth
              onChange={(value) => onUpdateField("recipientName", value)}
              errorMessage={getFieldError("RecipientName")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.customerNumber")}
              value={recipientData?.recipientNumber ?? ""}
              name={"recipientNumber"}
              fullWidth
              onChange={(value) => onUpdateField("recipientNumber", value)}
              errorMessage={getFieldError("RecipientNumber")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={12} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("random.recipientEmail")}
              value={recipientData?.email ?? ""}
              name={"recipientEmail"}
              fullWidth
              onChange={(value) => onUpdateField("email", value)}
              errorMessage={getFieldError("RecipientEmail")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={12} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.addressOne")}
              value={recipientData?.addressLine1 ?? ""}
              name={"addressLine1"}
              fullWidth
              onChange={(value) => onUpdateField("addressLine1", value)}
              errorMessage={getFieldError("RecipientAddressLine1")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={12} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.addressTwo")}
              value={recipientData?.addressLine2 ?? ""}
              name={"addressLine2"}
              fullWidth
              onChange={(value) => onUpdateField("addressLine2", value)}
              errorMessage={getFieldError("RecipientAddressLine2")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.zipCode")}
              value={recipientData?.postalCode ?? ""}
              name={"postalCode"}
              fullWidth
              onChange={(value) => onUpdateField("postalCode", value)}
              errorMessage={getFieldError("RecipientPostalCode")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.city")}
              value={recipientData?.city ?? ""}
              name={"city"}
              fullWidth
              onChange={(value) => onUpdateField("city", value)}
              errorMessage={getFieldError("RecipientCity")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <AutocompleteSelect
              label={t("invoices.countryCode")}
              value={recipientData?.countryCode ?? ""}
              options={COUNTRY_CODE_OPTIONS}
              onChange={(value) => onUpdateField("countryCode", value)}
              errorMessage={getFieldError("RecipientCountryCode")}
              disabled={!isEditable}
            />
          </Col>
        </Row>
      </SlideOutHeight>

      {(errorMessage || error) && (
        <Alert
          type="error"
          message={errorMessage}
          title="Ändringar av mottagruppgifter kunde inte sparas"
        />
      )}
    </div>
  );
};

export default InvoiceRecipientDetailsSection;
